import { useCallback, useMemo, useState } from 'react'
import { useOrg } from '@/admin/hooks/use-org'

interface UseDismissedStatusPillsResult {
  /** Status-pill keys the user has dismissed for this table. */
  dismissedKeys: string[]
  /** Hide a status pill (persisted per tenant + table). */
  dismiss: (key: string) => void
  /** Restore a previously dismissed status pill. */
  restore: (key: string) => void
  /** Restore every dismissed status pill for this table. */
  clear: () => void
}

const KEY_PREFIX = 'raven:dismissed-status-pills:v1'
const LEGACY_KEY_PREFIX = 'stream-x:dismissed-status-pills:v1'

function getStorageKey(tableId: string, tenantSlug: string | null): string | null {
  // Mirror useTableColumnPreferences: refuse to read/write without a known
  // tenant slug so dismissed pills never bleed across tenants for a user
  // with multiple memberships.
  if (!tenantSlug) return null
  return `${KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function getLegacyStorageKey(tableId: string, tenantSlug: string | null): string | null {
  if (!tenantSlug) return null
  return `${LEGACY_KEY_PREFIX}:${tenantSlug}:${tableId}`
}

function readDismissed(storageKey: string | null, legacyKey: string | null = null): string[] {
  if (typeof window === 'undefined' || !storageKey) return []

  try {
    let raw = window.localStorage.getItem(storageKey)
    // Migrate from the legacy stream-x: key once per browser, then delete it.
    if (!raw && legacyKey) {
      raw = window.localStorage.getItem(legacyKey)
      if (raw) {
        window.localStorage.setItem(storageKey, raw)
        window.localStorage.removeItem(legacyKey)
      }
    }
    if (!raw) return []

    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

function writeDismissed(storageKey: string | null, keys: string[]) {
  if (typeof window === 'undefined' || !storageKey) return

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(keys))
  } catch {
    // Ignore storage failures so table controls keep working in private mode.
  }
}

export function useDismissedStatusPills(tableId: string): UseDismissedStatusPillsResult {
  const { data: org } = useOrg()
  const tenantSlug = org?.organisation?.tenant_slug ?? null

  const storageKey = useMemo(() => getStorageKey(tableId, tenantSlug), [tableId, tenantSlug])
  const legacyStorageKey = useMemo(
    () => getLegacyStorageKey(tableId, tenantSlug),
    [tableId, tenantSlug],
  )

  const [dismissedKeys, setDismissedKeys] = useState<string[]>(() =>
    readDismissed(storageKey, legacyStorageKey),
  )

  // Reset during render when the storage key (tenant/table) changes.
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey)
  if (storageKey !== prevStorageKey) {
    setPrevStorageKey(storageKey)
    setDismissedKeys(readDismissed(storageKey, legacyStorageKey))
  }

  const dismiss = useCallback(
    (key: string) => {
      setDismissedKeys((current) => {
        if (current.includes(key)) return current
        const next = [...current, key]
        writeDismissed(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  const restore = useCallback(
    (key: string) => {
      setDismissedKeys((current) => {
        if (!current.includes(key)) return current
        const next = current.filter((k) => k !== key)
        writeDismissed(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  const clear = useCallback(() => {
    setDismissedKeys((current) => {
      if (current.length === 0) return current
      writeDismissed(storageKey, [])
      return []
    })
  }, [storageKey])

  return { dismissedKeys, dismiss, restore, clear }
}
